import { BLAST_AT_S } from './bgData';

/**
 * The Holy Hand Grenade's one sound: a choir, a beat of silence, and the bang.
 *
 * The bang is BLAST_AT_S into the clip, so the clip is *started early* — the
 * choir is the fuse burning, and the explosion you hear is the explosion you
 * see. Scheduled on the audio clock rather than with setTimeout, because a
 * timer can be a frame or three late and the mismatch is audible against a
 * flash.
 */
const CLIP = '/assets/holy-hand-grenade.mp3';

/** Turn this if the bang is too loud against the harp. 1 = the clip as drawn. */
export const BLAST_GAIN = 0.1;

let ctx: AudioContext | null = null;
let clip: AudioBuffer | null = null;
/** The take that is playing or scheduled, so dousing the church can stop it. */
let live: AudioBufferSourceNode | null = null;
/** performance.now() the bang is due at, if it was called for before the
 *  decode finished — a fuse is 3s and a first-visit fetch can outlast it. */
let due: number | null = null;
let dead = false;

async function load(on: AudioContext): Promise<void> {
  try {
    const res = await fetch(CLIP);
    if (!res.ok) throw new Error(`${res.status}`);
    clip = await on.decodeAudioData(await res.arrayBuffer());
  } catch {
    dead = true;
    return;
  }
  if (due !== null) play(due);
}

/**
 * Open the audio context while a gesture is still in hand — a press on the
 * dome — and start fetching. Safe to call on every press.
 */
export function armGrenade(): void {
  if (dead) return;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) {
      dead = true;
      return;
    }
    ctx = new AC();
    void load(ctx);
  }
  if (ctx.state === 'suspended') void ctx.resume();
}

/**
 * Cut it off: the church went dark, so the fuse is out and the choir with it.
 * Safe at any point — before the clip has even downloaded, or mid-bar.
 */
export function stopGrenade(): void {
  due = null;
  if (!live) return;
  const src = live;
  live = null;
  src.onended = null;
  try {
    src.stop();
  } catch {
    // already finished; stop() on a source that never started would throw
  }
  src.disconnect();
}

/** Play so that the bang lands at `bangAt` (a performance.now() reading). */
export function fireGrenade(bangAt: number): void {
  if (dead) return;
  if (!ctx || !clip) {
    due = bangAt;
    return;
  }
  play(bangAt);
}

function play(bangAt: number): void {
  due = null;
  if (!ctx || !clip) return;
  // Relighting the church twice in a second must not stack two choirs.
  stopGrenade();
  const lead = (bangAt - performance.now()) / 1000;
  // Too little runway left (a slow first fetch) — drop that much of the choir
  // rather than the bang, which is the part that has to be on time. A fraction
  // of a millisecond of lateness is not a shortfall though: relighting the
  // church aims the bang at exactly BLAST_AT_S away, and shaving the choir's
  // attack off for the microseconds the arithmetic took would be silly.
  const behind = BLAST_AT_S - lead;
  const offset = behind > 0.02 ? behind : 0;
  const when = ctx.currentTime + Math.max(0, lead - BLAST_AT_S + offset);
  const src = ctx.createBufferSource();
  src.buffer = clip;
  const gain = ctx.createGain();
  gain.gain.value = BLAST_GAIN;
  src.connect(gain).connect(ctx.destination);
  src.onended = () => {
    if (live === src) live = null;
    src.disconnect();
    gain.disconnect();
  };
  src.start(when, offset);
  live = src;
}

/** Test seam: what the next fire() would schedule, without scheduling it. */
export function grenadeAudioState(): { ready: boolean; dead: boolean; due: number | null } {
  return { ready: clip !== null, dead, due };
}
